import {
  faBuilding,
  faClockRotateLeft,
  faGear,
  faUser
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAgenciesList } from '~/api/queries/agency'
import { useCurrentUserInfos } from '~/api/queries/user'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Button } from '~/components/Global/Interactions/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { contentWrapper } from '~/pages/styles/Dashboard.css'
import { defaultBlocks } from '../../blocks'
import { DashboardProvider } from '../../context/DashboardContext'
import { useDashboardOnboarding } from '../../hooks/useDashboardOnboarding'
import type {
  DashboardBlock as DashboardBlockType,
  DashboardTarget
} from '../../types'
import { ApplicantExperiencesBlock } from '../blocks/ApplicantExperiencesBlock/ApplicantExperiencesBlock'
import { CalendarBlock } from '../blocks/CalendarBlock/CalendarBlock'
import { LastActivityBlock } from '../blocks/LastActivityBlock/LastActivityBlock'
import { LastApplicationsBlock } from '../blocks/LastApplicationsBlock/LastApplicationsBlock'
import { LastOpenOrdersBlock } from '../blocks/LastOpenOrdersBlock/LastOpenOrdersBlock'
import { NextActionsBlock } from '../blocks/NextActionsBlock/NextActionsBlock'
import { DashboardBlock } from '../DashboardBlock/DashboardBlock'
import { DashboardConfiguration } from '../DashboardConfiguration/DashboardConfiguration'
import {
  mainColumn,
  sideColumn,
  sideColumnsWrapper,
  simpleHeaderWrapper
} from './Dashboard.css'

const renderBlockContent = (block: DashboardBlockType): ReactElement | null => {
  switch (block.type) {
    case 'last-applications':
      return <LastApplicationsBlock />
    case 'next-actions':
      return <NextActionsBlock />
    case 'calendar':
      return <CalendarBlock />
    case 'last-open-orders':
      return <LastOpenOrdersBlock />
    case 'applicant-experiences':
      return <ApplicantExperiencesBlock />
    default:
      return null
  }
}

const getTargetIcon = (target: DashboardTarget) => {
  return target === 'self' ? faUser : faBuilding
}

type DashboardProps = {
  target: DashboardTarget
  onTargetChange: (nextTarget: DashboardTarget) => void
}

export const Dashboard = ({
  target,
  onTargetChange
}: DashboardProps): ReactElement => {
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const blocksContainerRef = useRef<HTMLDivElement | null>(null)

  const { activeAgency } = useActiveAgency()

  useEffect(() => {
    if (activeAgency && target !== 'self') {
      const expectedTarget = `agency-${activeAgency.id}` as DashboardTarget
      if (target !== expectedTarget) {
        onTargetChange(expectedTarget)
      }
    }
  }, [activeAgency, onTargetChange, target])

  const { data: usersData, isLoading: isLoadingUser } = useCurrentUserInfos()
  const user = usersData?.[0] ?? null
  const { data: agenciesData, isLoading: isLoadingAgencies } = useAgenciesList()
  const updateConfig = useUpdateConfig('dashboard.blocks')
  const { data: savedConfiguration, isLoading } = useConfig('dashboard.blocks')
  const { startTour } = useDashboardOnboarding()

  let agencyIds = user?.agencies?.map((agency) => agency.id) ?? []
  if (agenciesData && user?.groups.some((group) => group.id === 1)) {
    agencyIds = agenciesData.map((agency) => agency.id)
  }

  const getAgencyIdFromTarget = useCallback(
    (t: DashboardTarget): number | null => {
      if (typeof t === 'string' && t.startsWith('agency-')) {
        const id = Number.parseInt(t.replace('agency-', ''), 10)
        return Number.isNaN(id) ? null : id
      }
      return agencyIds.length > 0 ? (agencyIds[0] ?? null) : null
    },
    [agencyIds]
  )

  const agencyData = agenciesData?.find(
    (agency) => agency.id === getAgencyIdFromTarget(target)
  )

  const agencyId = useMemo(() => {
    if (typeof target === 'string' && target.startsWith('agency-')) {
      const id = Number.parseInt(target.replace('agency-', ''), 10)
      return Number.isNaN(id) ? null : id
    }
    return null
  }, [target])

  const userIds = useMemo(() => {
    if (target === 'self' && user?.id) {
      return [user.id]
    }
    if (
      typeof target === 'string' &&
      target.startsWith('agency-') &&
      agencyData?.users
    ) {
      return agencyData.users.map((u) => u.id)
    }
    return []
  }, [target, user?.id, agencyData])

  const isUserIdsLoading = useMemo(
    () => isLoadingUser || isLoadingAgencies,
    [isLoadingUser, isLoadingAgencies]
  )

  const getNextTarget = useCallback((): DashboardTarget => {
    if (agencyIds.length === 0) return 'self'
    if (activeAgency) {
      return target === 'self' ? `agency-${activeAgency.id}` : 'self'
    }
    if (target === 'self') return `agency-${agencyIds[0]}`
    if (agencyId !== null) {
      const currentIndex = agencyIds.indexOf(agencyId)
      if (currentIndex === agencyIds.length - 1) return 'self'
      return `agency-${agencyIds[currentIndex + 1]}`
    }
    return 'self'
  }, [target, agencyIds, agencyId, activeAgency])

  const getTargetDisplayName = useCallback((): string => {
    if (target === 'self') return 'Moi'
    if (typeof target === 'string' && target.startsWith('agency-')) {
      const id = Number.parseInt(target.replace('agency-', ''), 10)
      const agency = agenciesData?.find((a) => a.id === id)
      return agency?.name ?? 'Agence'
    }
    return agencyData?.name ?? 'Agence'
  }, [target, agenciesData, agencyData])

  const handleReorderBlocks = useCallback(
    (draggedBlockId: string, targetOrder: number) => {
      const mergedBlocks = defaultBlocks.map((block) => {
        const savedBlock = savedConfiguration?.find((b) => b.id === block.id)
        return savedBlock ? { ...block, ...savedBlock } : block
      })
      const sortedBlocks = mergedBlocks.sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
      )
      const draggedIndex = sortedBlocks.findIndex(
        (block) => block.id === draggedBlockId
      )
      const targetIndex = sortedBlocks.findIndex(
        (block) => block.position === targetOrder
      )
      if (draggedIndex === -1 || targetIndex === -1) return
      const [draggedBlock] = sortedBlocks.splice(draggedIndex, 1)
      if (draggedBlock) sortedBlocks.splice(targetIndex, 0, draggedBlock)
      const updatedBlocks = sortedBlocks.map((block, index) => ({
        ...block,
        position: index + 1
      }))
      updateConfig.mutate({
        value: updatedBlocks.map(({ id, position, isVisible }) => ({
          id,
          position,
          isVisible
        }))
      })
    },
    [updateConfig, savedConfiguration]
  )

  if (isLoading || !savedConfiguration) {
    return <Loader />
  }

  const sortedConfiguration = [...savedConfiguration].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
  const blockDefinitions: DashboardBlockType[] = []
  sortedConfiguration.forEach((savedBlock) => {
    const defaultBlock = defaultBlocks.find(
      (block) => block.id === savedBlock.id
    )
    if (defaultBlock) {
      blockDefinitions.push({ ...defaultBlock, ...savedBlock })
    }
  })
  defaultBlocks.forEach((defaultBlock) => {
    const existsInConfig = savedConfiguration.some(
      (saved) => saved.id === defaultBlock.id
    )
    if (!existsInConfig) blockDefinitions.push(defaultBlock)
  })

  return (
    <DashboardProvider
      userIds={userIds}
      isUserIdsLoading={isUserIdsLoading}
      agencyId={agencyId}
    >
      <div className={contentWrapper}>
        <div className={sideColumnsWrapper}>
          <div className={mainColumn}>
            <div className={simpleHeaderWrapper}>
              {new Date().getHours() < 4 || new Date().getHours() > 18
                ? 'Bonsoir'
                : 'Bonjour'}{' '}
              {user?.firstname} 👋
              <ButtonGroup>
                <Button
                  variation={ButtonVariation.Neutral}
                  onClick={() => onTargetChange(getNextTarget())}
                  buttonSize="small"
                  data-onboarding="target-toggle"
                >
                  <FontAwesomeIcon icon={getTargetIcon(target)} />
                  {getTargetDisplayName()}
                </Button>
                <Button
                  variation={ButtonVariation.Neutral}
                  onClick={() => setIsConfigOpen(true)}
                  buttonSize="small"
                  title="Configurer le tableau de bord"
                  narrow
                  data-onboarding="config-button"
                >
                  <FontAwesomeIcon icon={faGear} />
                </Button>
              </ButtonGroup>
            </div>
            <div ref={blocksContainerRef} data-onboarding="main-blocks">
              {blockDefinitions
                .filter((block) => block.isVisible)
                .map((block) => {
                  const content = renderBlockContent(block)
                  if (!content) return null
                  return (
                    <DashboardBlock
                      key={block.id}
                      onReorder={handleReorderBlocks}
                      {...block}
                    >
                      {content}
                    </DashboardBlock>
                  )
                })}
            </div>
          </div>
          <div className={sideColumn} data-onboarding="last-activity">
            <DashboardBlock
              title="Éléments créés"
              icon={faClockRotateLeft}
              draggable={false}
              id="history"
              position={1}
              isVisible={true}
            >
              <LastActivityBlock />
            </DashboardBlock>
          </div>
        </div>
        <DashboardConfiguration
          isOpen={isConfigOpen}
          onClose={() => setIsConfigOpen(false)}
          blocks={blockDefinitions}
          onRestartTour={startTour}
        />
      </div>
    </DashboardProvider>
  )
}
